import type { AuthStatus, UserPrivate } from '@runway/shared';
import { create } from 'zustand';
import { api, setUnauthorizedHandler } from '../net/api';

export type SessionStatus = 'loading' | 'anon' | 'authed';

export type SessionState = {
  status: SessionStatus;
  user: UserPrivate | null;
  devLoginEnabled: boolean;
  googleEnabled: boolean;
  error: string | null;
  /** Re-fetch /api/auth/status. */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** Replace the cached user (e.g. after PATCH /api/me). */
  setUser: (user: UserPrivate | null) => void;
};

export const useSession = create<SessionState>()((set) => ({
  status: 'loading',
  user: null,
  devLoginEnabled: false,
  googleEnabled: false,
  error: null,

  async refresh() {
    try {
      const s = await api.get<AuthStatus>('/api/auth/status');
      set({
        status: s.authenticated ? 'authed' : 'anon',
        user: s.user,
        devLoginEnabled: s.devLoginEnabled,
        googleEnabled: s.googleEnabled,
        error: null,
      });
    } catch (err) {
      set({ status: 'anon', user: null, error: err instanceof Error ? err.message : 'Could not reach the server' });
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      set({ status: 'anon', user: null });
    }
  },

  setUser(user) {
    set({ user, status: user ? 'authed' : 'anon' });
  },
}));

// Any 401 from the API means the cookie is gone/expired: drop to anon.
setUnauthorizedHandler(() => {
  if (useSession.getState().status === 'authed') useSession.setState({ status: 'anon', user: null });
});
